import { ZendeskTicketComment } from "@kuika/kuika-cl-model";
import { EZendeskCustomField, IZendeskTicket } from "@kuika/kuika-cl-model/src/models/zendesk";
import _ from "lodash";
import { ChangeEvent, RefObject, createRef } from "react";
import DateService from "../../../services/date-service";
import ZendeskService from "../../../services/zendesk-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import TrashAttachmentSvg from "../../../svgs/small-icons/trash-attachment.svg";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import "./ticket-viewer.scss";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { TicketThreadSkeleton } from "./helpdesk-skeleton";
import TicketComment from "./ticket-comment";
import { getTicketIdColor } from "./ticket-id-color";
import ZendeskStatusBadge, { ZendeskTicketStatus } from "./zendesk-status-badge";
import { getTicketDisplayStatus } from "./zendesk-ticket-filter";

interface Props {
  ticket: IZendeskTicket;
  isTicketRequester: boolean;
}

interface State {
  message: string;
  attachments: string[];
  isSendCommentLoading: boolean;
}

class TicketViewer extends PureSdComponent<Props & IWithSDProps, State> {
  private MAX_MESSAGE_COUNT: number = 1000;

  private MAX_ATTACHMENTS: number = 4;

  private ALLOWED_MIME_TYPES: string[] = ["image/png", "image/jpg", "image/jpeg", "image/svg+xml"];

  private fileInputRef: RefObject<HTMLInputElement>;

  private messagesAreaRef: RefObject<HTMLDivElement>;

  /** Guards the scroll-to-bottom so it fires on a new message, not on every render. */
  private scrolledCommentCount: number = 0;

  constructor(props: Props & IWithSDProps) {
    super(props);
    this.state = {
      message: "",
      attachments: [],
      isSendCommentLoading: false
    };
    this.fileInputRef = createRef<HTMLInputElement>();
    this.messagesAreaRef = createRef<HTMLDivElement>();
  }

  private onChangeMessage = (_e: ChangeEvent<HTMLTextAreaElement>, value: string | number): void => {
    const message: string = String(value);
    if (message.length > this.MAX_MESSAGE_COUNT) return;
    this.setState({ message });
    this.updateReplyValues(message, this.state.attachments);
  };

  private updateReplyValues(message: string, attachments: string[]) {
    const reply = {
      ticketId: this.props.ticket.id.toString(),
      message,
      attachments
    };
    this.props.sd.updateTempHelpdeskReplyTicketValue(reply);
  }

  private onClickUpload = (): void => {
    // Say so rather than doing nothing: the drop path already warns at this limit.
    if (this.state.attachments.length >= this.MAX_ATTACHMENTS) {
      KMainFunctions.displayWarningNotification(
        `You can only upload ${this.MAX_ATTACHMENTS} images at most. Remove one to add another.`
      );
      return;
    }
    this.fileInputRef.current?.click();
  };

  private scrollToLatestComment = (): void => {
    const children: Element[] = Array.from(this.messagesAreaRef.current?.children || []);
    children.at(-1)?.scrollIntoView();
  };

  private onClickSend = async () => {
    try {
      this.setState({ isSendCommentLoading: true });
      await ZendeskService.SendComment({
        ticketId: this.props.ticket.id.toString(),
        body: this.state.message,
        images: this.state.attachments
      });
      KMainFunctions.displaySuccessNotification("Message sent successfully");
      this.setState({ message: "", attachments: [] });
      await this.props.sd.loadTicketComments(this.props.ticket.id);
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isSendCommentLoading: false });
      this.props.sd.deleteTempHelpdeskReplyTicketValue(this.props.ticket.id.toString());
    }
  };

  private getBase64 = (file: File): Promise<string> => {
    const reader: FileReader = new FileReader();

    return new Promise((resolve) => {
      reader.onload = (e) => {
        if (!e.target) return;
        if (typeof e.target.result !== "string") return;
        resolve(e.target.result);
      };
      reader.readAsDataURL(file);
    });
  };

  private onClickDeleteAttachment = (e: React.MouseEvent, attachment: string) => {
    e.stopPropagation();

    this.setState({
      attachments: this.state.attachments.filter((a) => a !== attachment)
    });
  };

  private onDragEnter = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  private onDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  private processFileList = async (fileList: FileList): Promise<void> => {
    if (this.state.attachments.length >= this.MAX_ATTACHMENTS) {
      KMainFunctions.displayWarningNotification(
        "You can't upload any more images. You can only upload 4 images at most. Consider removing some of the images if you want to upload another one."
      );
      return;
    }

    const files: File[] = Array.from(fileList).filter((file) => {
      const isAllowedMimeType: boolean = this.ALLOWED_MIME_TYPES.includes(file.type);

      if (!isAllowedMimeType) {
        KMainFunctions.displayWarningNotification(`File type is not supported for file "${file.name}."`);
      }

      return isAllowedMimeType;
    });

    if (fileList.length > files.length) {
      KMainFunctions.displayWarningNotification(
        "Only images with the following extensions are supported: png, jpg, jpeg, svg."
      );
    }

    if (files.length <= 0) return;

    if (this.state.attachments.length + files.length > this.MAX_ATTACHMENTS) {
      KMainFunctions.displayWarningNotification(
        "You can only upload 4 images at most. Files that exceed the limit will be ignored."
      );
    }

    const droppedFiles: string[] = await Promise.all(files.map((f) => this.getBase64(f)));
    const attachments: string[] = [...this.state.attachments];

    droppedFiles.forEach((url: string) => {
      if (attachments.length >= this.MAX_ATTACHMENTS) return;
      if (attachments.includes(url)) return;
      attachments.push(url);
    });

    this.setState({ attachments }, () => {
      this.updateReplyValues(this.state.message, attachments);
    });
  };

  private onDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    await this.processFileList(e.dataTransfer.files);
  };

  private onChangeFileInput = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    await this.processFileList(e.target.files);
    e.target.value = "";
  };

  componentDidMount(): void {
    // The thread arrives with the ticket — `selectTicket` fetches both in one window — so this
    // mounts on data that is already there and only has to park the view at the newest message.
    this.syncScrollToLatest();
    this.setReplyValues();
  }

  /** A reply appends to the provider's thread; follow it down, and only then. */
  componentDidUpdate(): void {
    this.syncScrollToLatest();
  }

  private syncScrollToLatest = (): void => {
    const count: number = this.props.sd.selectedTicketComments.length;
    if (count === this.scrolledCommentCount) return;
    this.scrolledCommentCount = count;
    this.scrollToLatestComment();
  };

  private setReplyValues() {
    const reply = this.props.sd?.tempHelpdeskReplyTicketValues?.find(
      (r) => r.ticketId === this.props.ticket.id.toString()
    );
    if (reply) {
      this.setState({
        message: reply.message ?? "",
        attachments: reply.attachments ?? []
      });
    }
  }

  render() {
    const ticketStatus: ZendeskTicketStatus | undefined = getTicketDisplayStatus(this.props.ticket);
    const domain = this.props.ticket.customFields.find((f) => f.id === EZendeskCustomField.DOMAIN);
    const appName = this.props.ticket.customFields.find((f) => f.id === EZendeskCustomField.APP_NAME);
    const screenName = this.props.ticket.customFields.find((f) => f.id === EZendeskCustomField.SCREEN_NAME);
    const showCreateMessage =
      ["new", "open", "pending"].includes(this.props.ticket.status) && this.props.isTicketRequester;
    const comments: ZendeskTicketComment[] = this.props.sd.selectedTicketComments;
    const isThreadPending: boolean = this.props.sd.isTicketCommentsLoading && comments.length === 0;

    return (
      <div className="ticket-viewer-container">
        {/*
          The overlay is for SENDING only — an action the user just took, over content they are
          still looking at. Waiting for the thread itself is a load, and gets the placeholder
          below instead of a dimmed copy of the previous ticket's messages.
        */}
        <KuikaSpinnerSmall className="ticket-viewer-spinner" spinning={this.state.isSendCommentLoading}>
          <div className="ticket-viewer-messages">
            <div className="ticket-viewer-messages-header">
              <div className="ticket-viewer-messages-header-subject-and-info">
                <div data-testid="ticketSubject" className="ticket-viewer-subject">
                  {this.props.ticket.subject}
                </div>
                <div className="ticket-viewer-status-and-date">
                  <KuikaBadge
                    content={`#${this.props.ticket.id}`}
                    color={getTicketIdColor(this.props.ticket.id)}
                    variant="subtle"
                  />
                  {ticketStatus && <ZendeskStatusBadge status={ticketStatus} withCanonicalTestId />}
                  <div className="ticket-viewer-header-date">
                    {DateService.getHumanizedDate(this.props.ticket.createdAt)}
                  </div>
                </div>
              </div>
              <div data-testid="contextInfo" className="ticket-viewer-context-info">
                {domain?.value} / {appName?.value} / {screenName?.value}
              </div>
            </div>
            <div ref={this.messagesAreaRef} className="ticket-viewer-comments">
              {isThreadPending ? (
                <TicketThreadSkeleton />
              ) : (
                comments.map((comment: ZendeskTicketComment) => (
                  <TicketComment comment={comment} key={`${comment.authorId}-${comment.createdAt}`} />
                ))
              )}
            </div>
          </div>
          {showCreateMessage && (
            <div className="ticket-viewer-create-message">
              <div className="ticket-viewer-create-message-inputs">
                <div className="ticket-viewer-textarea-container">
                  <KuikaInput
                    data-testid="currentTicketNewMessageInput"
                    onDragEnter={this.onDragEnter}
                    onDragOver={this.onDragOver}
                    onDrop={this.onDrop}
                    className="ticket-viewer-textarea"
                    type="textarea"
                    borderless
                    resizable={false}
                    placeholder="Type here..."
                    value={this.state.message}
                    onChange={this.onChangeMessage}
                  />
                </div>

                <input
                  className="ticket-viewer-upload-file-input"
                  data-testid="ticketReplyFileInput"
                  ref={this.fileInputRef}
                  type="file"
                  multiple
                  accept={this.ALLOWED_MIME_TYPES.join(",")}
                  onChange={this.onChangeFileInput}
                />
                {this.state.attachments.length > 0 && (
                  <div
                    className="ticket-viewer-attachments"
                    onDragEnter={this.onDragEnter}
                    onDragOver={this.onDragOver}
                    onDrop={this.onDrop}
                    onClick={this.onClickUpload}
                  >
                    {this.state.attachments.map((a: string) => (
                      <button
                        key={a}
                        className="ticket-viewer-attachment-button"
                        onClick={(e) => this.onClickDeleteAttachment(e, a)}
                      >
                        <img className="ticket-viewer-attachment-image" src={a} alt="" />
                        <span className="ticket-viewer-attachment-trash-backdrop">
                          <TrashAttachmentSvg />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="ticket-viewer-create-message-actions">
                <button
                  data-testid="currentTicketUpload"
                  className="ticket-viewer-upload"
                  onClick={this.onClickUpload}
                  onDragEnter={this.onDragEnter}
                  onDragOver={this.onDragOver}
                  onDrop={this.onDrop}
                >
                  <KuikaSvg name="image" width={15} height={15} />
                  Add images
                </button>
                <span className="ticket-viewer-textarea-count">
                  {this.state.message.length}/{this.MAX_MESSAGE_COUNT}
                </span>
                <KuikaButton
                  data-testid="currentTicketSendButton"
                  uppercase
                  size="md"
                  onClick={this.onClickSend}
                  disabled={_.isEmpty(this.state.message)}
                >
                  Send
                </KuikaButton>
              </div>
            </div>
          )}
        </KuikaSpinnerSmall>
      </div>
    );
  }

  /** The thread lives in the provider now, so provider changes have to reach this component. */
  checkNeedRender = (): boolean => {
    return true;
  };
}

const ticketViewer = withSD<Props>(TicketViewer);
export { ticketViewer as TicketViewer };
